import type {
  DashboardModel, DashboardBody, DashboardVisual,
  DashboardGlobalCriterion, DashboardDrillThrough,
  DashboardDrillTargetType,
  DashboardOperator, DashboardDataType,
  DashboardCriterionBinding, DashboardBindType,
} from './types'
import { DASHBOARD_THEME_DEFAULT, normalizeDashboardThemeColor } from '@twasta/visual-engine'

const VALID_BIND_TYPES: DashboardBindType[] = ['global', 'constant', 'unbound']

const VALID_DRILL_TARGET_TYPES: DashboardDrillTargetType[] = [
  'dashboard', 'visual', 'self',
]

const VALID_OPERATORS: DashboardOperator[] = [
  '=', '!=', '>', '>=', '<', '<=', 'between', 'in', 'like',
]
const VALID_DATA_TYPES: DashboardDataType[] = ['string', 'number', 'date']

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

// DASHBOARD_ID always tracks the obj_name 1:1 — the file on disk is
// `model_<obj_name>.json` and renaming an object should be an out-of-band
// rename (Workbench prototype flow), not an in-editor edit.
export function toDashboardId(objName: string): string {
  return (objName || 'DASH').toUpperCase().replace(/[^A-Z0-9_]/g, '_')
}

// Every dashboard authored in Twasta is type 'S' (Standard). The field
// exists for downstream consumers that branch on dashboard kind.
export const DASHBOARD_TYPE_STANDARD = 'S'

export function blankDashboardModel(objName: string): DashboardModel {
  return {
    DASHBOARD: {
      DASHBOARD_ID: toDashboardId(objName),
      DASHBOARD_TYPE: DASHBOARD_TYPE_STANDARD,
      DASHBOARD_NAME: (objName || 'Dashboard').replace(/_/g, ' '),
      DASHBOARD_DESCR: '',
      CREATED_BY: 'admin',
      CREATED_DATE: todayIso(),
      REFRESH_INTERVAL_SECONDS: 0,
      THEME_COLOR: DASHBOARD_THEME_DEFAULT,
      GLOBAL_CRITERIA: [],
      VISUALS: [],
      DRILL_THROUGH: [],
    },
  }
}

export function nextVisualId(existing: DashboardVisual[]): string {
  // Pick the next VIS_NNN id that isn't taken.
  const used = new Set(existing.map((v) => v.VISUAL_ID))
  for (let i = 1; i < 10000; i++) {
    const candidate = `VIS_${String(i).padStart(3, '0')}`
    if (!used.has(candidate)) return candidate
  }
  return `VIS_${Date.now()}`
}

export function blankVisual(existing: DashboardVisual[]): DashboardVisual {
  return {
    VISUAL_ID: nextVisualId(existing),
    VISUAL_NAME: 'New Tile',
    POSITION: { X: 0, Y: 0, WIDTH: 6, HEIGHT: 4 },
    VISUAL_CONFIG_REF: '',
  }
}

export function blankBinding(visualParam: string): DashboardCriterionBinding {
  return { VISUAL_PARAM: visualParam, BIND_TYPE: 'unbound' }
}

export function blankCriterion(): DashboardGlobalCriterion {
  return {
    PARAM_NAME: '',
    PROMPT_LABEL: '',
    DEFAULT_VALUE: '',
    DATA_TYPE: 'string',
    OPERATOR: '=',
    BROADCAST_TO: [],
  }
}

export function blankDrillThrough(): DashboardDrillThrough {
  return {
    SOURCE_VISUAL_ID: '',
    TARGET_TYPE: 'dashboard',
    TARGET_DASHBOARD_ID: '',
    TARGET_VISUAL_ID: '',
    PARAM_MAPPING: {},
  }
}

// Make a loaded JSON safe to drive the editor. Tolerates the generator
// emitting bare fields without the DASHBOARD wrapper (mirrors the backend's
// _normalize behavior). Always returns a usable model — never throws.
export function sanitizeDashboardModel(
  raw: unknown,
  objName: string
): DashboardModel {
  if (!raw || typeof raw !== 'object') return blankDashboardModel(objName)
  const r = raw as Record<string, unknown>

  // Accept either { DASHBOARD: {...} } or bare fields at the root.
  const body =
    (r.DASHBOARD && typeof r.DASHBOARD === 'object'
      ? (r.DASHBOARD as Record<string, unknown>)
      : r) || {}

  const visuals = sanitizeVisuals(body.VISUALS)
  const criteria = sanitizeCriteria(body.GLOBAL_CRITERIA, visuals)
  const drillThrough = sanitizeDrillThrough(body.DRILL_THROUGH)

  const dashboard: DashboardBody = {
    // DASHBOARD_ID is always derived from objName — the LLM may emit a
    // divergent value but we ignore it so id/file/obj_name stay in sync.
    DASHBOARD_ID: toDashboardId(objName),
    // Twasta-authored dashboards are always type 'S' (Standard).
    DASHBOARD_TYPE: DASHBOARD_TYPE_STANDARD,
    DASHBOARD_NAME:
      typeof body.DASHBOARD_NAME === 'string' && body.DASHBOARD_NAME
        ? body.DASHBOARD_NAME
        : (objName || 'Dashboard').replace(/_/g, ' '),
    DASHBOARD_DESCR:
      typeof body.DASHBOARD_DESCR === 'string' ? body.DASHBOARD_DESCR : '',
    CREATED_BY:
      typeof body.CREATED_BY === 'string' && body.CREATED_BY
        ? body.CREATED_BY
        : 'admin',
    CREATED_DATE:
      typeof body.CREATED_DATE === 'string' && body.CREATED_DATE
        ? body.CREATED_DATE
        : todayIso(),
    REFRESH_INTERVAL_SECONDS:
      typeof body.REFRESH_INTERVAL_SECONDS === 'number'
        ? body.REFRESH_INTERVAL_SECONDS
        : Number(body.REFRESH_INTERVAL_SECONDS) || 0,
    // Rebuilt like every other key — an unknown value becomes 'default'.
    THEME_COLOR: normalizeDashboardThemeColor(body.THEME_COLOR),
    GLOBAL_CRITERIA: criteria,
    VISUALS: visuals,
    DRILL_THROUGH: drillThrough,
  }

  return { DASHBOARD: dashboard }
}

function sanitizeVisuals(raw: unknown): DashboardVisual[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  const out: DashboardVisual[] = []
  for (const v of raw) {
    if (!v || typeof v !== 'object') continue
    const o = v as Record<string, unknown>
    let visualId = (typeof o.VISUAL_ID === 'string' && o.VISUAL_ID)
      || nextVisualId(out)
    // De-duplicate ids
    while (seen.has(visualId)) {
      visualId = nextVisualId(out)
    }
    seen.add(visualId)
    const pos = (o.POSITION && typeof o.POSITION === 'object'
      ? (o.POSITION as Record<string, unknown>)
      : {}) as Record<string, unknown>
    const entry: DashboardVisual = {
      VISUAL_ID: visualId,
      VISUAL_NAME:
        (typeof o.VISUAL_NAME === 'string' && o.VISUAL_NAME) || visualId,
      POSITION: {
        X: clampUnit(pos.X, 0, 11.75, 0),
        Y: clampUnit(pos.Y, 0, 999, 0),
        WIDTH: clampUnit(pos.WIDTH, 0.5, 12, 6),
        HEIGHT: clampUnit(pos.HEIGHT, 0.5, 24, 4),
      },
      VISUAL_CONFIG_REF:
        typeof o.VISUAL_CONFIG_REF === 'string' ? o.VISUAL_CONFIG_REF : '',
      CRITERIA_BINDINGS: sanitizeBindings(o.CRITERIA_BINDINGS),
    }
    // Report tile: keep the discriminator + report ref. Absent/unknown
    // TILE_KIND stays a visual tile (models predating report tiles).
    if (String(o.TILE_KIND ?? '').trim().toLowerCase() === 'report') {
      entry.TILE_KIND = 'report'
      entry.REPORT_OBJ = typeof o.REPORT_OBJ === 'string' ? o.REPORT_OBJ : ''
    }
    // Header visibility — only persist the non-default (false); absent = shown.
    if (o.SHOW_HEADER === false) entry.SHOW_HEADER = false
    out.push(entry)
  }
  return out
}

function sanitizeBindings(raw: unknown): DashboardCriterionBinding[] {
  if (!Array.isArray(raw)) return []
  const out: DashboardCriterionBinding[] = []
  for (const b of raw) {
    if (!b || typeof b !== 'object') continue
    const o = b as Record<string, unknown>
    const visualParam =
      typeof o.VISUAL_PARAM === 'string' ? o.VISUAL_PARAM : ''
    if (!visualParam) continue
    const bindType: DashboardBindType =
      VALID_BIND_TYPES.includes(o.BIND_TYPE as DashboardBindType)
        ? (o.BIND_TYPE as DashboardBindType)
        : 'unbound'
    const entry: DashboardCriterionBinding = {
      VISUAL_PARAM: visualParam,
      BIND_TYPE: bindType,
    }
    if (bindType === 'global' && typeof o.GLOBAL_PARAM === 'string') {
      entry.GLOBAL_PARAM = o.GLOBAL_PARAM
    }
    if (bindType === 'constant') {
      entry.CONSTANT_VALUE =
        typeof o.CONSTANT_VALUE === 'string'
          ? o.CONSTANT_VALUE
          : o.CONSTANT_VALUE != null
            ? String(o.CONSTANT_VALUE)
            : ''
    }
    out.push(entry)
  }
  return out
}

// Reconcile each global criterion's BROADCAST_TO with the per-tile
// CRITERIA_BINDINGS, but only for tiles that actually have bindings.
//
// Rule per tile T:
//   - If T.CRITERIA_BINDINGS is non-empty, T's membership in every
//     global's BROADCAST_TO is fully determined by its bindings: T is
//     in BROADCAST_TO[X] iff at least one binding points to global X.
//   - If T.CRITERIA_BINDINGS is empty, T is left untouched in every
//     BROADCAST_TO list — preserves the legacy "Criteria tab toggles"
//     behaviour for dashboards that don't use bindings yet.
//
// Returns a new body; does not mutate the input.
export function syncBroadcastFromBindings(body: DashboardBody): DashboardBody {
  // Tile id → set of GLOBAL_PARAM names this tile binds to.
  const tileGlobals = new Map<string, Set<string>>()
  for (const v of body.VISUALS) {
    const bindings = v.CRITERIA_BINDINGS || []
    if (bindings.length === 0) continue   // legacy / untouched tile
    const set = new Set<string>()
    for (const b of bindings) {
      if (b.BIND_TYPE === 'global' && b.GLOBAL_PARAM) set.add(b.GLOBAL_PARAM)
    }
    tileGlobals.set(v.VISUAL_ID, set)
  }
  const nextCriteria = body.GLOBAL_CRITERIA.map((c) => {
    const next: string[] = []
    const seen = new Set<string>()
    // Existing entries: keep tiles that either have no bindings
    // (legacy) or whose bindings still target this global.
    for (const tileId of c.BROADCAST_TO) {
      const tg = tileGlobals.get(tileId)
      if (tg === undefined) {
        // Tile has no bindings — preserve existing membership.
        if (!seen.has(tileId)) { next.push(tileId); seen.add(tileId) }
      } else if (tg.has(c.PARAM_NAME)) {
        if (!seen.has(tileId)) { next.push(tileId); seen.add(tileId) }
      }
      // else: tile has bindings but none target this global → drop it
    }
    // Newly bound tiles: add any tile whose bindings target this global
    // but isn't already in the list.
    for (const [tileId, tg] of tileGlobals) {
      if (tg.has(c.PARAM_NAME) && !seen.has(tileId)) {
        next.push(tileId)
        seen.add(tileId)
      }
    }
    return next.length === c.BROADCAST_TO.length
      && next.every((id, i) => id === c.BROADCAST_TO[i])
        ? c
        : { ...c, BROADCAST_TO: next }
  })
  return { ...body, GLOBAL_CRITERIA: nextCriteria }
}

function sanitizeCriteria(
  raw: unknown,
  visuals: DashboardVisual[]
): DashboardGlobalCriterion[] {
  if (!Array.isArray(raw)) return []
  const validVisualIds = new Set(visuals.map((v) => v.VISUAL_ID))
  const out: DashboardGlobalCriterion[] = []
  for (const c of raw) {
    if (!c || typeof c !== 'object') continue
    const o = c as Record<string, unknown>
    const dataType =
      VALID_DATA_TYPES.includes(o.DATA_TYPE as DashboardDataType)
        ? (o.DATA_TYPE as DashboardDataType)
        : 'string'
    const operator =
      VALID_OPERATORS.includes(o.OPERATOR as DashboardOperator)
        ? (o.OPERATOR as DashboardOperator)
        : '='
    const broadcastRaw = Array.isArray(o.BROADCAST_TO) ? o.BROADCAST_TO : []
    // Drop entries that don't reference a known visual id; the editor lets
    // the user re-select these from the multi-checkbox UI.
    const broadcast = (broadcastRaw as unknown[])
      .filter((x): x is string => typeof x === 'string')
      .filter((x) => validVisualIds.has(x))
    const crit: DashboardGlobalCriterion = {
      PARAM_NAME: typeof o.PARAM_NAME === 'string' ? o.PARAM_NAME : '',
      PROMPT_LABEL: typeof o.PROMPT_LABEL === 'string' ? o.PROMPT_LABEL : '',
      DEFAULT_VALUE:
        typeof o.DEFAULT_VALUE === 'string'
          ? o.DEFAULT_VALUE
          : o.DEFAULT_VALUE != null
            ? String(o.DEFAULT_VALUE)
            : '',
      DATA_TYPE: dataType,
      OPERATOR: operator,
      BROADCAST_TO: broadcast,
    }
    // Optional enrichments (F2 lookup, picklist options, item_change, and the
    // checks: mandatory + validations) pass through untouched — rebuilding
    // with only the core keys would silently strip them on every load, and
    // the next Save would erase them from the model file.
    for (const key of [
      'lookup', 'lookup_filter', 'lookup_filter_rules', 'lookup_sql',
      'lookup_title', 'lookup_sql_data_col', 'item_change', 'options',
      'mandatory', 'validations',
    ]) {
      if (o[key] != null) crit[key] = o[key]
    }
    out.push(crit)
  }
  return out
}

function sanitizeDrillThrough(raw: unknown): DashboardDrillThrough[] {
  if (!Array.isArray(raw)) return []
  const out: DashboardDrillThrough[] = []
  for (const d of raw) {
    if (!d || typeof d !== 'object') continue
    const o = d as Record<string, unknown>
    const mapping: Record<string, string> = {}
    if (o.PARAM_MAPPING && typeof o.PARAM_MAPPING === 'object') {
      for (const [k, v] of Object.entries(o.PARAM_MAPPING as Record<string, unknown>)) {
        if (typeof v === 'string') mapping[k] = v
      }
    }
    // TARGET_TYPE: absent/unknown normalizes to 'dashboard' so models saved
    // before target types existed keep working unchanged.
    const rawType =
      typeof o.TARGET_TYPE === 'string' ? o.TARGET_TYPE.trim().toLowerCase() : ''
    const targetType: DashboardDrillTargetType =
      VALID_DRILL_TARGET_TYPES.includes(rawType as DashboardDrillTargetType)
        ? (rawType as DashboardDrillTargetType)
        : 'dashboard'
    const targetDashboardId =
      typeof o.TARGET_DASHBOARD_ID === 'string' ? o.TARGET_DASHBOARD_ID : ''
    const targetVisualId =
      typeof o.TARGET_VISUAL_ID === 'string' ? o.TARGET_VISUAL_ID : ''
    // Drop entries with no valid target for their type ('self' targets the
    // current dashboard, so it never needs an explicit target id).
    if (targetType === 'dashboard' && !targetDashboardId) continue
    if (targetType === 'visual' && !targetVisualId) continue
    const entry: DashboardDrillThrough = {
      SOURCE_VISUAL_ID:
        typeof o.SOURCE_VISUAL_ID === 'string' ? o.SOURCE_VISUAL_ID : '',
      TARGET_TYPE: targetType,
      TARGET_DASHBOARD_ID: targetDashboardId,
      TARGET_VISUAL_ID: targetVisualId,
      PARAM_MAPPING: mapping,
    }
    // The trigger column — only persisted when set (absent = "Any column").
    const sourceColumn =
      typeof o.SOURCE_COLUMN === 'string' ? o.SOURCE_COLUMN.trim() : ''
    if (sourceColumn) entry.SOURCE_COLUMN = sourceColumn
    out.push(entry)
  }
  return out
}

// Positions/sizes are in 12-column grid units but may be fractional in
// quarter-unit steps (fine-grained designer resizing) — snap, then clamp.
function clampUnit(
  raw: unknown,
  min: number,
  max: number,
  fallback: number
): number {
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
        ? Number(raw)
        : NaN
  if (!Number.isFinite(n)) return fallback
  const q = Math.round(n * 4) / 4
  if (q < min) return min
  if (q > max) return max
  return q
}
